import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import type { PracticeScope } from '../../types.js';

const SCOPES: { value: PracticeScope; label: string; aria: string }[] = [
    { value: 'lh', label: 'LH', aria: 'Left hand only' },
    { value: 'rh', label: 'RH', aria: 'Right hand only' },
    { value: 'both', label: 'Both', aria: 'Both hands' },
];

/**
 * Three-way segmented control for practice mode scope (LH / RH / Both).
 * Visible only when Practice mode is active.
 *
 * @fires scope-change - detail: { scope: PracticeScope }
 */
@customElement('scope-selector')
export class ScopeSelector extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-flex;
                align-items: center;
                height: 37px;
                background-color: #F2F2F7;
                border-radius: 37px;
                padding: 2px;
                user-select: none;
            }

            :host([disabled]) {
                opacity: 0.5;
                pointer-events: none;
            }

            button {
                min-width: 44px;
                height: 33px;
                padding: 0 8px;
                border-radius: 33px;
                font-size: 11px;
                font-weight: 600;
                color: #3C3C43;
                background: transparent;
                transition: background-color 0.2s ease, color 0.2s ease;
            }

            button[aria-checked="true"] {
                background-color: #007AFF;
                color: white;
            }

            button:focus-visible {
                outline: 2px solid #007AFF;
                outline-offset: 1px;
            }
        `,
    ];

    @property({ type: String, reflect: true })
    scope: PracticeScope = 'both';

    @property({ type: Boolean, reflect: true })
    disabled = false;

    private _select(scope: PracticeScope): void {
        if (this.disabled || scope === this.scope) return;
        this.scope = scope;
        this.dispatchEvent(
            new CustomEvent<{ scope: PracticeScope }>('scope-change', {
                bubbles: true,
                composed: true,
                detail: { scope },
            })
        );
    }

    render() {
        return html`
            <div role="radiogroup" aria-label="Practice scope">
                ${SCOPES.map(
                    (s) => html`
                        <button
                            type="button"
                            role="radio"
                            aria-checked=${s.value === this.scope}
                            aria-label=${s.aria}
                            tabindex=${s.value === this.scope ? 0 : -1}
                            @click=${() => this._select(s.value)}
                        >
                            ${s.label}
                        </button>
                    `
                )}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'scope-selector': ScopeSelector;
    }
}
